import React, {useState} from "react";
import {Alert, Box, Card, CardContent, CircularProgress, Divider, TextField, Typography,} from "@mui/material";
import axios from "axios";
import VaccinationAdministration from "./VaccinationAdministration";
import VaccinationTracking from "./VaccinationTracking";
import {baseUrl} from "../Univ";
const VaccinationAdministrationPage = ({
                                           selectedPatient: initialPatient = null,
                                       }) => {
    const [selectedPatient, setSelectedPatient] =
        useState(initialPatient);
    const [patientSearch, setPatientSearch] =
        useState("");
    const [patientResults, setPatientResults] =
        useState([]);
    const [patientSearchLoading, setPatientSearchLoading] =
        useState(false);
    const [error, setError] =
        useState("");
    /* ============================================================
       PATIENT SEARCH
       ============================================================ */
    const searchPatients = async (value) => {
        setPatientSearch(value);
        setError("");
        if (!value || value.trim().length < 2) {
            setPatientResults([]);
            return;
        }
        try {
            setPatientSearchLoading(true);
            const response = await axios.get(
                `${baseUrl}pat-search/`,
                {
                    params: {
                        search: value,
                    },
                }
            );
            const data = Array.isArray(response.data)
                ? response.data
                : response.data.results || [];
            setPatientResults(data);
        } catch (err) {
            console.error(
                "Patient search error:",
                err
            );
            setPatientResults([]);
        } finally {
            setPatientSearchLoading(false);
        }
    };
    /* ============================================================
       PATIENT SELECTED
       ============================================================ */
    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setPatientSearch(
            `${patient.patient || ""} - ${patient.pat_id || ""}`
        );
        setPatientResults([]);
    };
    /* ============================================================
       CLEAR PATIENT
       ============================================================ */
    const clearPatient = () => {
        setSelectedPatient(null);
        setPatientSearch("");
        setPatientResults([]);
    };
    return (
        <Box className="vaccination-page">

            {/* =====================================================
                HEADER
                ===================================================== */}

            <Typography
                variant="h5"
                fontWeight={700}
                sx={{mb: 3}}
            >
                Vaccination Administration
            </Typography>


            {/* =====================================================
                PATIENT SEARCH
                ===================================================== */}

            <Card
                sx={{
                    mb: 3,
                    position: "relative",
                    overflow: "visible",
                }}
            >

                <CardContent
                    sx={{
                        position: "relative",
                        overflow: "visible",
                    }}
                >

                    <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{mb: 2}}
                    >
                        Select Patient
                    </Typography>

                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            zIndex: 1000,
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Search Patient"
                            value={patientSearch}
                            onChange={(e) =>
                                searchPatients(
                                    e.target.value
                                )
                            }
                            placeholder="Search by Patient Name, Patient ID or Mobile"
                            autoComplete="off"
                        />

                        {patientSearchLoading && (
                            <CircularProgress
                                size={22}
                                sx={{
                                    position: "absolute",
                                    right: 15,
                                    top: 16,
                                }}
                            />
                        )}

                        {patientResults.length > 0 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "calc(100% + 5px)",
                                    left: 0,
                                    right: 0,
                                    background: "#fff",
                                    border: "1px solid #d5dbe3",
                                    borderRadius: "8px",
                                    boxShadow:
                                        "0 10px 30px rgba(0,0,0,.20)",
                                    maxHeight: 320,
                                    overflowY: "auto",
                                    zIndex: 999999,
                                }}
                            >

                                {patientResults.map((patient) => (
                                    <Box
                                        key={patient.id}
                                        onClick={() =>
                                            handlePatientSelect(
                                                patient
                                            )
                                        }
                                        sx={{
                                            padding: "12px 16px",
                                            cursor: "pointer",
                                            borderBottom:
                                                "1px solid #edf0f3",
                                            "&:hover": {
                                                background:
                                                    "#f3f7f5",
                                            },
                                        }}
                                    >

                                        <Typography
                                            fontWeight={600}
                                            fontSize={14}
                                        >
                                            {patient.patient || "-"}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            ID:{" "}
                                            {patient.pat_id || "-"}
                                        </Typography>

                                        {patient.mobile && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Mobile:{" "}
                                                {patient.mobile}
                                            </Typography>
                                        )}

                                    </Box>
                                ))}

                            </Box>
                        )}

                    </Box>

                </CardContent>

            </Card>


            {/* =====================================================
                ERROR
                ===================================================== */}

            {error && (
                <Alert
                    severity="error"
                    sx={{mb: 3}}
                >
                    {error}
                </Alert>
            )}


            {/* =====================================================
                SELECTED PATIENT
                ===================================================== */}

            {selectedPatient && (
                <Card sx={{mb: 3}}>

                    <CardContent>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            {selectedPatient.my_title || ""}
                            {" "}
                            {selectedPatient.patient || ""}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Patient ID:{" "}
                            {selectedPatient.pat_id ||
                                selectedPatient.id ||
                                "-"}
                        </Typography>

                        <Divider sx={{my: 2}}/>

                        <div className="row g-3">

                            <div className="col-md-3">

                                <strong>
                                    Patient ID
                                </strong>

                                <div>
                                    {selectedPatient.pat_id || "-"}
                                </div>

                            </div>

                            <div className="col-md-3">

                                <strong>
                                    Patient Name
                                </strong>

                                <div>
                                    {selectedPatient.patient || "-"}
                                </div>

                            </div>

                            <div className="col-md-2">

                                <strong>
                                    Gender
                                </strong>

                                <div>
                                    {selectedPatient.gender || "-"}
                                </div>

                            </div>

                            <div className="col-md-2">

                                <strong>
                                    Age
                                </strong>

                                <div>
                                    {selectedPatient.age || "-"}
                                </div>

                            </div>

                            <div className="col-md-2">

                                <strong>
                                    ABHA
                                </strong>

                                <div>
                                    {selectedPatient.abha || "-"}
                                </div>

                            </div>

                        </div>

                    </CardContent>

                </Card>
            )}


            {/* =====================================================
                VACCINATION ADMINISTRATION
                ===================================================== */}

            {selectedPatient && (
                <Card sx={{mb: 3}}>

                    <CardContent>

                        <VaccinationAdministration
                            appointment={{
                                patient_id:
                                selectedPatient.id,
                                patient:
                                selectedPatient,
                            }}

                            onSaved={() => {
                                // Refresh tracking
                                // after successful administration
                            }}
                        />

                    </CardContent>

                </Card>
            )}


            {/* =====================================================
                VACCINATION TRACKING
                ===================================================== */}

            {selectedPatient && (
                <VaccinationTracking
                    patientId={
                        selectedPatient.id
                    }
                />
            )}

        </Box>
    );
};
export default VaccinationAdministrationPage;